import { CURRENT_TERMS_VERSION, PWZ_REQUIRED } from '@/lib/pwz';

/** Minimal profile shape needed for the registration / terms gate. */
export type OnboardingProfile = {
  pwz?: string | null;
  pwz_status?: string | null;
  terms_accepted_at?: unknown;
  terms_version?: string | null;
} | null;

/** True when the profile still lacks a submitted PWZ (when required). The
 * backend sets `pwz_status` together with `pwz` on the first submission, so the
 * status is the marker; pilot accounts created before PWZ_REQUIRED have
 * neither and are asked for it at /onboarding. Admins are exempt (not every
 * founder is a doctor). */
export function profileNeedsPwz(
  profile: OnboardingProfile,
  opts: { admin?: boolean } = {},
): boolean {
  if (!PWZ_REQUIRED || opts.admin) return false;
  return !profile?.pwz_status;
}

/** True when the profile never accepted the terms, or accepted an older version. */
export function profileNeedsTerms(profile: OnboardingProfile): boolean {
  return (
    !profile?.terms_accepted_at ||
    profile.terms_version !== CURRENT_TERMS_VERSION
  );
}

/** True when the profile accepted an older terms version than the current one. */
export function profileHasOutdatedTerms(profile: OnboardingProfile): boolean {
  return Boolean(profile?.terms_accepted_at) && profileNeedsTerms(profile);
}

/** True when the clinician still needs terms acceptance (and PWZ when required). */
export function profileNeedsOnboarding(
  profile: OnboardingProfile,
  opts: { admin?: boolean } = {},
): boolean {
  if (!profile) return true;
  if (profileNeedsTerms(profile)) return true;
  return profileNeedsPwz(profile, opts);
}
